import { useEffect } from 'react';
import { createCloseChoreography, CLOSE_START_MS } from './modalCloseChoreography';

/** Lifted at the tap but the keyboard was NEVER registered open (KAN-646): wait longer, settle WITHOUT blurring. */
const UNSEEN_RELEASE_MS = 1500;

  // COORDINATED CLOSE (KAN-552 1c; one beat since KAN-930). The keyboard has
  // been closed for CLOSE_START_MS (140) → at ONE moment start: the card's
  // glide back to rest (padding-top on KB_MOTION) or the rest card's body
  // release, the scroll-back of the body loan, and the height tween. The field
  // BLUR waits out the KAN-561 window from the keyboard's close (300ms plain,
  // 700ms password / one-time-code) and the geometry is only handed back
  // (spacer released, scrollTop exact, freeze finished — never on
  // transitionend alone) after max(KB_MOTION + 80, that blur): a keyboard
  // that returns first cancels the blur AND undoes the release (`abort`).
  // Keyed on keyboardOpen (not focus): iOS can dismiss the keyboard while the
  // field keeps focus. Never seen open (KAN-646) → longer wait, no blur.
export default function useModalCoordinatedClose({
  dialogEl, keyboardOpen, anchor, setAnchor, shrinkTick, restReleased, captureLoan, stopScrollBack, refs: R,
}) {
  const {
    freezeRef, tuckRef, shrinkRef, loanRef, closingRef, finishTimerRef, cancelScrollRef,
    suppressUntilRef, keyboardSeenRef, closedAtRef, closeCtlRef, tapRestedRef,
  } = R;
  useEffect(() => {
    if (!dialogEl || keyboardOpen) return undefined;
    if (anchor?.releasing != null) return undefined;
    const f = freezeRef.current;
    // Nothing to coordinate: not lifted, no held loan (builders / plain cards).
    const tk = tuckRef.current;
    const sh = shrinkRef.current && shrinkRef.current.isActive() ? shrinkRef.current : null;
    if (!anchor && !loanRef.current && !(tk && tk.isTucked()) && !sh) return undefined;
    if (!anchor && closingRef.current) return undefined; // a rest-card close is already running
    if (restReleased && tapRestedRef.current && closingRef.current) return undefined; // the action beat arrived mid-close
    // Only a keyboard that was SEEN open and then closed ends the typing
    // session (blur). Never seen → longer wait, keep the field focused.
    const seen = keyboardSeenRef.current;
    const ctl = createCloseChoreography({
      blurField: seen,
      dialogEl,
      freeze: f,
      tuck: tk,
      shrink: sh,
      closedAt: closedAtRef.current || null,
      anchor,
      refs: { loanRef, closingRef, finishTimerRef, cancelScrollRef, suppressUntilRef },
      stopScrollBack,
      captureLoan,
      setAnchor,
    });
    // The action beat (restReleased) sends a tap-rested card to the centre now.
    const delay = restReleased && tapRestedRef.current ? 0 : (seen ? CLOSE_START_MS : UNSEEN_RELEASE_MS);
    const timer = setTimeout(() => { closeCtlRef.current = ctl; ctl.run(); }, delay);
    return () => clearTimeout(timer);
  // Refs are stable; `shrinkTick` re-plans when a shrink starts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardOpen, anchor, dialogEl, captureLoan, stopScrollBack, shrinkTick, restReleased, setAnchor]);
}
